import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, inject, Inject, Optional, signal, Signal, ViewChild, WritableSignal, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA  } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { JournalSetup } from '@app/_interfaces/journal.interface';
import { CacheService } from '@app/_services/cache.service';
import { JournalStore } from '@app/_stores';
import { Subject, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import { AlertService } from '@app/_services/alert.service';
import { UtilsService } from '@app/_services/utils.service';
import { CollaboratorService } from '@app/_services/collaborator.service';
import { BlockedUserService } from '@app/_services/blocked-user.service';
import { getTotalCollaboratorsCount, getRoleByUid } from '@app/_utils/members-model.utils';
import { MatMenuModule } from '@angular/material/menu';
import { CollaboratorComponent } from '@app/components/addons/collaborators/collaborator/collaborator.component';
import { InputTextComponent } from '@app/components/shared/inputs/input-text/input-text.component';
import { InputHashtagComponent } from '@app/components/shared/inputs/input-hashtag/input-hashtag.component';
import { HashtagComponent } from '@app/components/addons/hashtags/hashtag/hashtag.component';

import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { InputDatePeriodComponent } from '@app/components/shared/inputs/input-date-period/input-date-period.component';
import { InputTimeComponent } from '@app/components/shared/inputs/input-time/input-time.component';
import { getLocalDateString } from '@app/_services/date';
import { InputReminderComponent } from '@app/components/shared/inputs/input-reminder/input-reminder.component';
import { notOnlyWhitespace } from '@app/_directives/form-validator.directive';
import { CollaboraterRole, CollaboraterStatus } from '@app/_types/generic.type';
import { DependencyService } from '@app/_services/dependency.service';
import { SettingsService } from '@app/_services/settings.service';
import { AnalyticsService } from '@app/_services/analytics.service';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { FirestoreCollection } from '@app/_enums/firestore-collection.enum';
import { MessageViewComponent } from '@app/components/addons/message/message-view/message-view.component';
// JournalEntriesComponent loaded dynamically to break circular dependency
import { log } from '@app/_services/logger';
import { CommonSetupTypeComponent } from '@app/components/shared/common-setup-type/common-setup-type.component';
import { FeatureGuideDialogComponent } from '@app/components/common/feature-guides/feature-guide-dialog/feature-guide-dialog.component';
import { ModalService } from '@app/_services/modal.service';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { isDesktopViewport } from '@app/_configs/layout.config';

@Component({
  selector: 'app-journal-setup-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatMenuModule,
    InputTextComponent,
    InputHashtagComponent,
    InputDatePeriodComponent,
    SvgComponent,
    InputTimeComponent,
    InputReminderComponent,
    CommonSetupTypeComponent,
  ],
  templateUrl: './journal-setup-form.component.html',
  styleUrl: './journal-setup-form.component.scss'
})

export class JournalSetupFormComponent implements OnInit, OnDestroy {

  @ViewChild('jSetupForm') jSetupForm!: NgForm;
  @ViewChild(InputHashtagComponent) inputHashtagComponent!: InputHashtagComponent;
  unSubscribe = new Subject<void>();
  journalSetupForm: FormGroup;
  journalSetupInitial: JournalSetup;
  readonly journalStore = inject(JournalStore);
  submitted: boolean = false;
  mode: WritableSignal<'new' | 'edit'> = signal('new');
  membersVersion: WritableSignal<number> = signal(0); // Track members changes for reactivity
  dataSetupId: WritableSignal<string> = signal('');
  hashtagInputActive: WritableSignal<boolean> = signal(false);
  setup: Signal<JournalSetup> = computed(() => {
    const setupId = this.dataSetupId();
    return this.journalStore.idToSetup()[setupId] || {} as JournalSetup;
  });
  collabCount: Signal<number> = computed(() => {
    // For new entities, read from form control (updates live as collaborators are added)
    // For existing entities, read from store (which reflects saved state)
    this.membersVersion(); // Subscribe to members changes for reactivity
    if (this.mode() === 'new') {
      const formMembers = this.journalSetupForm?.get('members')?.value;
      if (!formMembers) return 0;
      return getTotalCollaboratorsCount(formMembers);
    }
    const m = this.setup().members;
    if (!m) return 0;
    return getTotalCollaboratorsCount(m);
  });
  isCollab: Signal<boolean> = computed(() => {
    return this.collabCount() > 0;
  });

  // Show collaborate button for: new entities (owner can add collaborators) OR existing entities with collaborators
  showCollaborateBtn: Signal<boolean> = computed(() => {
    return this.mode() === 'new' || this.isCollab();
  });

  role: Signal<CollaboraterRole> = computed(() => {
    if (this.mode() === 'new') {
      return 'owner';
    } else if (this.setup().uid === this.cc.user.uid) {
      return 'owner';
    } else if (this.setup().members) {
      return getRoleByUid(this.setup().members, this.cc.user.uid) || 'viewer';
    } else {
      return 'viewer';
    }
  });

  deleted: Signal<boolean> = computed(() => {
    if (this.mode() === 'new') return false;
    const deletedAt = this.setup().deletedAt;
    if (!deletedAt) return false;
    return true;
  });

  // Check if the entity owner is blocked (for Block/Unblock button)
  isOwnerBlocked: Signal<boolean> = computed(() => {
    const ownerEmail = this.setup().members?.ownerEmail;
    if (!ownerEmail) return false;
    return this.blockedUserService.isBlocked(ownerEmail);
  });

  deletedAtString: Signal<string> = computed(() => {
    const deletedAt = this.setup().deletedAt;
    if (!deletedAt) return '';
    return this.ss.t('common_deletedAt', { date: this.ss.getFormattedDate(deletedAt, 'd MMM yyyy'), time: this.ss.getFormattedDate(deletedAt, 'h:mm a') });
  });

  featureStatus = computed(() => {
    const setup = this.setup();
    if (this.mode() === 'new' || !setup.startDate || !setup.endDate) return 'active';
    return this.ds.getFeatureStatus(setup.startDate, setup.endDate);
  });

  screenEnterTime: Signal<Date> = signal(new Date());

  readonly screenWidth = signal<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  private readonly resizeHandler = () => this.screenWidth.set(window.innerWidth);

  repeatDates: Signal<string[]> = computed(() => {
    const setup = this.setup();
    if (!setup || this.mode() === 'new' || !setup?.repeat) return [];
    return this.ds.getAllDatesOfEntity(setup, 'journal', this.cc.user.uid);
  });

  /** Whether this setup has any entries (actions) or scheduled repeat dates. Works for both scheduled and unscheduled. */
  hasEntries: Signal<boolean> = computed(() => {
    const setup = this.setup();
    if (!setup || this.mode() === 'new') return false;
    const actions = this.journalStore.setupToActions()[setup.id];
    if (!!actions && actions.length > 0) return true;
    return this.repeatDates().length > 0;
  });

  public data: { mode: 'new' | 'edit', value: JournalSetup };

  constructor(
    @Optional() public dialogRef: MatDialogRef<JournalSetupFormComponent> | null,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<JournalSetupFormComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: { mode: 'new' | 'edit', value: JournalSetup } | null,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) bottomSheetData: { mode: 'new' | 'edit', value: JournalSetup } | null,
    private fb: FormBuilder,
    public cc: CacheService,
    private alertService: AlertService,
    private utilsService: UtilsService,
    public dialog: MatDialog,
    public ds: DependencyService,
    public ss: SettingsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private collaboratorService: CollaboratorService,
    private blockedUserService: BlockedUserService,
    private router: Router
  ) {
    this.data = (dialogData || bottomSheetData)!;

    this.mode.set(this.data.mode);
    this.dataSetupId.set(this.data?.value?.id || '');
    log.debug('constructor', 'Journal setup form data', { value: this.data.value });
    this.journalSetupInitial = this.data.mode == 'new' ? this.initiateForm() : this.initiateForm(this.data.value);

    this.journalSetupForm = this.fb.group({
      id: new FormControl(this.journalSetupInitial.id, Validators.required),
      title: new FormControl(this.journalSetupInitial.title, [Validators.required, Validators.maxLength(200), notOnlyWhitespace()]),
      duration: new FormControl(this.journalSetupInitial.duration),
      startTime: new FormControl(this.journalSetupInitial.startTime),
      timeZoneId: new FormControl(this.journalSetupInitial.timeZoneId),
      isStartTimeSet: new FormControl(this.journalSetupInitial.startTime != null),
      isTzAffected: new FormControl(this.journalSetupInitial.timeZoneId != null),
      reminderAt: new FormControl(this.journalSetupInitial.reminderAt),
      repeat: new FormControl(this.journalSetupInitial.repeat),
      startDate: new FormControl(this.journalSetupInitial.startDate),
      endDate: new FormControl(this.journalSetupInitial.endDate),
      tags: new FormControl(this.journalSetupInitial.tags),
      uid: new FormControl(this.journalSetupInitial.uid, Validators.required),
      members: new FormControl(this.journalSetupInitial.members),
    });

    dialogRef?.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.closeDialog();
    });

    bottomSheetRef?.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.closeDialog();
    });
  }

  ngOnInit() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeHandler);
    }
    const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
    this.as.screenViewEvent('journalSetup', previousScreen);
    if (this.mode() === 'edit') {
      this.as.crudEvent('read', 'journalSetup', this.cc.user.uid === this.setup().uid ? 'private' : 'shared', this.setup().members?.memberUids.length || 0, 'notShared', this.role(), 'none');
    }
  }

  initiateForm(journalSetup?: JournalSetup): JournalSetup {
    return {
      id: journalSetup ? journalSetup.id : this.utilsService.getNewId(),
      title: journalSetup ? journalSetup.title : '',
      duration: journalSetup ? journalSetup.duration : 15,
      startTime: journalSetup ? journalSetup.startTime : null,
      timeZoneId: journalSetup ? journalSetup.timeZoneId : null,
      reminderAt: journalSetup ? journalSetup.reminderAt : [],
      repeat: journalSetup ? journalSetup.repeat : null,
      startDate: journalSetup ? journalSetup.startDate : null,
      endDate: journalSetup ? journalSetup.endDate : null,
      tags: journalSetup ? journalSetup.tags : [],
      uid: journalSetup ? journalSetup.uid : this.cc.user.uid,
      members: journalSetup ? journalSetup.members : { ownerEmail: this.cc.user.email, memberUids: [], collaborators: {} },
    }
  }

  cloneSetup() {
    const setupData = { ...this.setup() };
    setupData.id = this.utilsService.getNewId();
    setupData.title = this.ss.t('journalSetupAdd_cloneSetupTitleCopy', { journalTitle: setupData.title });
    setupData.startDate = getLocalDateString();
    setupData.endDate = null;
    setupData.members = { ownerEmail: this.cc.user.email, memberUids: [], collaborators: {} };

    this.journalSetupForm.get('id')?.setValue(setupData.id);
    this.journalSetupForm.get('title')?.setValue(setupData.title);
    this.journalSetupForm.get('startDate')?.setValue(setupData.startDate);
    this.journalSetupForm.get('endDate')?.setValue(setupData.endDate);
    this.journalSetupForm.get('members')?.setValue(setupData.members);
    this.journalSetupInitial = setupData;

    this.mode.set('new');
    this.dataSetupId.set('');
  }

  getFc(fcName: string): FormControl {
    return this.journalSetupForm.get(fcName) as FormControl;
  }

  hasChanges() {
    const initial = _.cloneDeep(this.journalSetupInitial) as any;
    const current = _.cloneDeep(this.journalSetupForm.value) as any;
    // Normalize: add form-derived fields missing from entity, remove entity-only fields
    initial.isStartTimeSet = initial.startTime != null;
    initial.isTzAffected = initial.timeZoneId != null;
    delete initial.timeZoneId;
    delete current.timeZoneId;
    return !_.isEqual(initial, current);
  }

  async openEntities() {
    // Dynamic import to break circular dependency:
    // journal-setup-form -> journal-entries -> journal-block -> journal-form -> journal-setup-form
    const { JournalEntriesComponent } = await import('../journal-entries/journal-entries.component');
    this.modalService.openLevel2(
      JournalEntriesComponent,
      { entityId: this.dataSetupId() },
      { disableClose: false, autoFocus: false }
    );
  }

  addCollaborator() {
    const entityData = this.mode() === 'new'
      ? this.buildCurrentSetupFromForm()
      : this.setup();

    const dialogRef = this.modalService.openLevel2(
      CollaboratorComponent,
      {
        collection: 'journalSetups',
        entityData: entityData,
        isPublic: false,
        isNewEntity: this.mode() === 'new',
        onMembersUpdatedForNewEntity: this.mode() === 'new'
          ? (updatedMembers: any) => {
              this.journalSetupForm.get('members')?.setValue(updatedMembers);
              // Also update journalSetupInitial for proper change detection
              this.journalSetupInitial = { ...this.journalSetupInitial, members: updatedMembers };
              // Trigger collabCount reactivity
              this.membersVersion.set(this.membersVersion() + 1);
            }
          : undefined,
      },
      { disableClose: true }
    );

    // For edit mode: increment membersVersion after dialog closes to trigger reactivity
    if (this.mode() === 'edit') {
      this.modalService.getClosedObservable(dialogRef).pipe(takeUntil(this.unSubscribe)).subscribe(() => {
        // Increment to trigger collabCount re-computation (reads from updated store)
        this.membersVersion.set(this.membersVersion() + 1);
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * Build current setup from form data (for new entities being created)
   */
  private buildCurrentSetupFromForm(): JournalSetup {
    const formValue = this.journalSetupForm.value;
    return {
      ...formValue,
      members: formValue.members || {
        ownerEmail: this.cc.user.email,
        memberUids: [],
        members: {},
        pendingInvites: {}
      }
    } as JournalSetup;
  }

  /**
   * Get dynamic block/unblock label
   */
  getBlockLabel(): string {
    return this.isOwnerBlocked() ? 'common_unblock' : 'common_block';
  }

  /**
   * Get collaborator icon based on whether there are collaborators
   */
  getCollaboratorIcon(): string {
    return this.isCollab() ? 'kebabMenuIsCollaborator' : 'kebabMenuAddCollaborator';
  }

  getTopBarCollaboratorIcon(): string {
    return this.isCollab() ? 'topBarIsCollaborator' : 'topBarAddCollaborator';
  }

  async removeCollab(action: CollaboraterStatus) {
    if (action === 'left') {
      const res = await this.alertService.confirm(this.ss.t('leaveJournal_title'), this.ss.t('leaveJournal_content'), this.ss.t('common_leave'), this.ss.t('common_buttonCancel'), 'color-11', 'color-7');
      if (!res?.confirm) return;
      try {
        this.cc.isLoading.set(true);
        const success = await this.collaboratorService.removeMemberFromEntity(
          this.setup().id, 'journalSetups', this.cc.user.email
        );
        if (!success) throw new Error('Failed to remove member from entity');
        this.close();
      } catch (error) {
        log.error('removeCollab', 'Failed to leave journal', error as Error);
        this.alertService.alert(this.ss.t('common_error'), this.ss.t('journal_leave_error'));
      } finally {
        this.cc.isLoading.set(false);
      }
    } else if (action === 'blocked') {
      const ownerEmail = this.setup().members?.ownerEmail;
      if (!ownerEmail) return;
      await this.blockedUserService.toggleBlockWithConfirmation(ownerEmail, 'journalSetups');
    }
  }

  isDesktopView(): boolean {
    return isDesktopViewport(this.screenWidth());
  }

  activateHashtagInput(): void {
    if (this.isDesktopView()) {
      this.inputHashtagComponent?.focusDesktopInput();
      return;
    }
    this.inputHashtagComponent?.openHashtagInputModal();
  }

  onHashtagsChanged(tags: string[]): void {
    this.journalSetupForm.get('tags')?.setValue(tags);
    this.cdr.markForCheck();
  }

  openHashtagsDialog() {
    this.activateHashtagInput();
  }

  private close(result?: any): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(result);
    }
  }

  async closeDialog() {
    if (this.hasChanges()) {
      const res = await this.alertService.confirm(this.ss.t('journalSetupDiscard_title'), this.ss.t('common_discardContent'), this.ss.t('common_save'), this.ss.t('common_discard'), 'color-35', 'color-11');
      if (!res) return;
      if (res.confirm === true) {
        this.save();
      } else if (res.confirm === false) {
        this.close();
      }
    } else {
      this.close();
    }
  }

  save() {
    this.submitted = true;
    if (this.journalSetupForm.invalid) {
      this.alertService.alert(this.ss.t('common_mandatoryAlertTitle'), this.ss.t('common_titleMandatory'), this.ss.t('common_ok'));
      return;
    };

    // Derive timeZoneId from form-only isTzAffected/isStartTimeSet flags
    const formValue = { ...this.journalSetupForm.value };
    if (!formValue.isStartTimeSet) {
      formValue.startTime = null;
      formValue.timeZoneId = null;
    } else {
      formValue.timeZoneId = formValue.isTzAffected ? Intl.DateTimeFormat().resolvedOptions().timeZone : null;
    }
    // Remove form-only fields not in entity
    delete formValue.isStartTimeSet;
    delete formValue.isTzAffected;

    if (this.mode() === 'new') {
      this.journalStore.addJournalSetup(formValue);
    } else if (this.mode() === 'edit') {
      const updatedSetup = { ...this.setup(), ...formValue };
      this.journalStore.updateJournalSetups([updatedSetup]);
    }
    this.close({ mode: this.mode(), id: formValue.id });
    this.router.navigate(['/features']);
  }

  async deleteJournalSetup() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('journalSetupEditDelete_title'),
      description: this.ss.t('journalSetupEditDelete_content'),
      yesButtonText: this.ss.t('common_delete'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    this.journalStore.deleteJournalSetup([this.setup()]);
    this.close();
  }

  openFeatureGuide() {
    const setupDialog = this.modalService.openLevel1(
      FeatureGuideDialogComponent,
      {
        entityType: 'journal'
      },
      { disableClose: true, autoFocus: false }
    );
  }


  openMessage() {
    const entity = this.setup();
    if (!entity?.members) return;

    const setupId = `ec_${entity.id}`;
    this.modalService.openLevel2(
      MessageViewComponent,
      { setupId, entityId: entity.id, entityCollection: FirestoreCollection.JournalSetups },
      { disableClose: true, autoFocus: false }
    );
  }

  async remove() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('removeConfirmation_title'),
      description: this.ss.t('removeConfirmation_content'),
      yesButtonText: this.ss.t('common_buttonRemove'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const journal = this.setup();
    journal.permaDeletedAt = new Date();
    this.journalStore.updateJournalSetups([journal]);
  };

  async restore() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('restoreConfirmation_title'),
      description: this.ss.t('restoreConfirmation_content'),
      yesButtonText: this.ss.t('common_restore'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const journal = this.setup();
    journal.deletedAt = null;
    this.journalStore.updateJournalSetups([journal]);
  };

  ngOnDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeHandler);
    }
    this.as.screenExitEvent('journalSetup', this.screenEnterTime());
    this.unSubscribe?.complete();
    this.unSubscribe?.next();
  }
}
